import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/auth'
import { lineSource } from './adminDashboard'

const trackDefaults = {
  points: [],
  lat: 0,
  lng: 0,
  zoom: 17
}

export const findClosestTrack = async (lat, lng) => {
  try {
    const response = await fetch(
      `${process.env.GATSBY_TYPESENSE_PROTOCOL}://${
        process.env.GATSBY_TYPESENSE_HOST
      }${
        process.env.GATSBY_TYPESENSE_PORT == "443"
          ? ""
          : `:${process.env.GATSBY_TYPESENSE_PORT}`
      }/collections/tracks/documents/search?q=*&query_by=name&sort_by=location(${lat},${lng}):asc&per_page=1`,
      {
        headers: {
          "X-TYPESENSE-API-KEY": `${process.env.GATSBY_TYPESENSE_READ_API}`,
        },
      }
    )
    const data = await response.json()
    const closestTrack = data?.hits[0]?.document

    if (!closestTrack) {
      throw new Error('No tracks found near the start/finish point. The racing line will be created without track association.')
    }

    // Calculate distance to check if within 10km
    const distance = getDistance(lat, lng, closestTrack.location[0], closestTrack.location[1])
    if (distance > 10) {
      throw new Error(`Closest track "${closestTrack.name}" is ${Math.round(distance)}km away. The racing line will be created without track association.`)
    }

    return closestTrack
  } catch (error) {
    if (error.message.includes('track')) {
      throw error // Re-throw our custom errors
    }
    console.error('Error finding closest track:', error)
    throw new Error('Failed to search for nearby tracks. The racing line will be created without track association.')
  }
}

// Haversine formula to calculate distance between two points
const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371 // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1)
  const dLon = deg2rad(lon2 - lon1)
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

const deg2rad = (deg) => {
  return deg * (Math.PI/180)
}

export const createDoc = async (trackValues, currentUser, props = {}) => {
  const linesDb = firebase.firestore().collection(`lines`)
  const { name = '', placeId = '', url = '', vehicle = '', vehicleId = '', telemetryId = '', lapTime = null, startFinishLat = null, startFinishLng = null } = props
  const t = { ...trackDefaults, ...trackValues }

  try {
    const doc = linesDb.doc()

    // Build document data, only including defined values
    const docData = {
      name,
      placeId,
      created: firebase.firestore.FieldValue.serverTimestamp(),
      location: new firebase.firestore.GeoPoint(+t.lat, +t.lng),
      url,
      zoom: t.zoom,
      points: t.points,
      user: currentUser.uid,
      likes: [],
      visibility: 'public',
      isCloseDetected: false,
      creationSource: lineSource({ telemetryId }).key
    }

    // Only add optional fields if they have defined values
    if (vehicle !== undefined && vehicle !== null) docData.vehicle = vehicle
    if (vehicleId !== undefined && vehicleId !== null) docData.vehicleId = vehicleId
    if (telemetryId !== undefined && telemetryId !== null) docData.telemetryId = telemetryId
    if (lapTime !== undefined && lapTime !== null) docData.lapTime = lapTime
    if (startFinishLat !== undefined && startFinishLat !== null) docData.startFinishLat = startFinishLat
    if (startFinishLng !== undefined && startFinishLng !== null) docData.startFinishLng = startFinishLng

    await doc.set(docData, { merge: true })

    console.log("Document created with id: ", doc.id)
    return doc.id
  }
  catch(error) {
    console.error("Error creating document: ", error);
    throw new Error('Failed to save the racing line. Please try again.')
  }
}

export const onImportLine = async ({ points, user, carInfo, telemetryId } = {}) => {
  if (!points?.length) {
    throw new Error('No points received from iRacing. Please try the import again.')
  }

  // Get current user from Firebase auth if not provided
  if (!user) {
    const currentUser = firebase.auth().currentUser
    if (!currentUser) {
      throw new Error('You must be logged in to import racing lines.')
    }
    user = currentUser
  }

  console.log(`Received ${points.length} points from iRacing import`)
  
  try {
    // Convert iRacing points to our format
    const newPoints = points.map((point, index) => {
      if (!point?.lat || !point?.lng) {
        console.error('Invalid point data:', point)
        throw new Error('Invalid point data received from iRacing. Please try the import again.')
      }

      // Determine point type based on inputs
      let type
      
      // If any braking at all, it's a brake point
      if (point.brake > 0) {
        type = 'brake'
      }
      // If no braking but throttle is applied, it's acceleration
      else if (point.throttle > 0) {
        type = 'acc'
      }
      // If no braking and no throttle, it's coasting
      else {
        type = 'coast'
      }
      
      const convertedPoint = {
        id: `iracing-${index}`,
        lat: point.lat,
        lng: point.lng,
        type,
        selected: false
      }

      // Only add fields if they exist (avoid undefined values for Firebase)
      if (point.speed !== undefined) convertedPoint.speed = point.speed
      if (point.altitude !== undefined) convertedPoint.altitude = point.altitude
      if (point.rpm !== undefined) convertedPoint.rpm = point.rpm
      if (point.gear !== undefined) convertedPoint.gear = point.gear
      if (point.throttle !== undefined) convertedPoint.throttle = point.throttle
      if (point.brake !== undefined) convertedPoint.brake = point.brake
      if (point.dist !== undefined) convertedPoint.dist = point.dist
      if (point.pct !== undefined) convertedPoint.pct = point.pct

      return convertedPoint
    })

    console.log(`First converted point:`, newPoints[0])
    
    // Try to find the closest track to the start/finish point
    const startPoint = newPoints[0]
    let closestTrack = null
    let trackError = null
    
    try {
      closestTrack = await findClosestTrack(startPoint.lat, startPoint.lng)
    } catch (error) {
      trackError = error
    }

    // Create new track with imported points
    const newTrack = {
      ...trackDefaults,
      points: newPoints,
      lat: startPoint.lat,
      lng: startPoint.lng,
      zoom: 17
    }
    
    // If we found a close track, use its details
    const docProps = {
      name: closestTrack ? closestTrack.name : 'Imported Racing Line',
      placeId: closestTrack?.placeId || '',
      url: closestTrack?.url || '/r',
      vehicle: carInfo?.carType || '',
      vehicleId: carInfo?.vehicleId || '',
      telemetryId: telemetryId || '',
      lapTime: points[0]?.lapTime || null,
      // Include start/finish line coordinates if provided (from GoPro detection)
      startFinishLat: carInfo?.startFinishLat,
      startFinishLng: carInfo?.startFinishLng
    }

    console.log('Creating document with car info:', { vehicle: carInfo?.carType, vehicleId: carInfo?.vehicleId, telemetryId, lapTime: points[0]?.lapTime })
    const lineId = await createDoc(newTrack, user, docProps)
    if (!lineId) {
      throw new Error('Failed to create the racing line. Please try again.')
    }

    // Construct the redirect URL
    // If no track was found, use the unmatched racing line path /r/{lineId}
    // Otherwise, use the track URL with /r/{lineId} format
    let redirectUrl
    if (!closestTrack) {
      redirectUrl = `/r/${lineId}`
    } else {
      redirectUrl = `${docProps.url}/r/${lineId}`
    }

    // If there was a non-fatal track error, include it in the response
    if (trackError) {
      return { lineId, newTrack, url: redirectUrl, warning: trackError.message }
    }

    return { lineId, newTrack, url: redirectUrl }
  } catch (error) {
    console.error('Import error:', error)
    throw error
  }
} 